import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import type { GroupsListResponse } from "@family/shared";

interface GroupPickerProps {
  familySlug: string;
  visibility: "everyone" | "groups";
  selectedGroupIds: string[];
  onVisibilityChange: (v: "everyone" | "groups") => void;
  onGroupsChange: (ids: string[]) => void;
  disabled?: boolean;
  /** Heading above the chips; defaults to "Share with". */
  label?: string;
}

export function GroupPicker({
  familySlug,
  visibility,
  selectedGroupIds,
  onVisibilityChange,
  onGroupsChange,
  disabled,
  label = "Share with",
}: GroupPickerProps) {
  const groupsQuery = useQuery<GroupsListResponse>({
    queryKey: ["groups", familySlug],
    queryFn: () =>
      apiFetch<GroupsListResponse>(`/families/${familySlug}/groups`),
  });

  const groups = groupsQuery.data?.groups ?? [];

  function toggleGroup(id: string) {
    if (selectedGroupIds.includes(id)) {
      onGroupsChange(selectedGroupIds.filter((g) => g !== id));
    } else {
      onGroupsChange([...selectedGroupIds, id]);
    }
  }

  return (
    <div>
      <label style={{ fontWeight: 700, fontSize: "0.85rem", marginBottom: 4, display: "block" }}>
        {label}
      </label>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            onVisibilityChange("everyone");
            onGroupsChange([]);
          }}
          style={{
            padding: "6px 14px",
            minHeight: 34,
            borderRadius: "var(--r-pill, 999px)",
            border: "1px solid",
            borderColor: visibility === "everyone" ? "var(--c-blue, #0058a3)" : "var(--c-border)",
            background: visibility === "everyone" ? "var(--c-blue, #0058a3)" : "transparent",
            color: visibility === "everyone" ? "#fff" : "var(--c-text)",
            fontWeight: visibility === "everyone" ? 700 : 400,
            fontSize: "0.85rem",
            cursor: "pointer",
          }}
        >
          Everyone
        </button>

        {groups.map((g) => {
          const selected = visibility === "groups" && selectedGroupIds.includes(g.id);
          return (
            <button
              key={g.id}
              type="button"
              disabled={disabled}
              onClick={() => {
                if (visibility !== "groups") {
                  onVisibilityChange("groups");
                  onGroupsChange([g.id]);
                } else {
                  toggleGroup(g.id);
                }
              }}
              style={{
                padding: "6px 14px",
                minHeight: 34,
                borderRadius: "var(--r-pill, 999px)",
                border: "1px solid",
                borderColor: selected ? "var(--c-blue, #0058a3)" : "var(--c-border)",
                background: selected ? "var(--c-blue, #0058a3)" : "transparent",
                color: selected ? "#fff" : "var(--c-text)",
                fontWeight: selected ? 700 : 400,
                fontSize: "0.85rem",
                cursor: "pointer",
              }}
            >
              {g.name}
              {g.memberCount != null && (
                <span style={{ opacity: 0.7, marginLeft: 4 }}>({g.memberCount})</span>
              )}
            </button>
          );
        })}
      </div>

      {visibility === "groups" && selectedGroupIds.length === 0 && (
        <p className="text-error" style={{ fontSize: "0.8rem" }}>
          Select at least one group
        </p>
      )}

      {groups.length === 0 && !groupsQuery.isLoading && (
        <p className="text-subtle" style={{ fontSize: "0.8rem" }}>
          No groups created yet. Ask an admin to create groups in Settings.
        </p>
      )}
    </div>
  );
}
